import { PT_VERBS, ptPresent } from './portuguese';
import { compound, type LanguageTenses, type Tense } from './tenses';

// ─────────────────────────────────────────────────────────────────────────────
// Portuguese (Brazil): every tense beyond the presente (its own topic). People
// are eu · tu (optional) · você/ele/ela · nós · vocês/eles/elas.
// Spelling kept: toquei / cheguei / dancei, toque / chegue / dance.
// Compound tenses use ter + -ado/-ido; escrever (escrito) and abrir (aberto)
// have irregular participles and are left out of them.
// ─────────────────────────────────────────────────────────────────────────────

const ALL = PT_VERBS;
const REG_PP = Object.fromEntries(Object.entries(PT_VERBS).filter(([v]) => v !== 'escrever' && v !== 'abrir'));

type T = 'ar' | 'er' | 'ir';
const type = (v: string) => v.slice(-2) as T;
const root = (v: string) => v.slice(0, -2);
const participle = (v: string) => root(v) + (type(v) === 'ar' ? 'ado' : 'ido');

/** Before e: c → qu, g → gu, ç → c. */
function soft(r: string): string {
  if (r.endsWith('ç')) return r.slice(0, -1) + 'c';
  if (r.endsWith('c')) return r.slice(0, -1) + 'qu';
  if (r.endsWith('g')) return r.slice(0, -1) + 'gu';
  return r;
}

const PERF = {
  ar: ['ei', 'aste', 'ou', 'amos', 'aram'], er: ['i', 'este', 'eu', 'emos', 'eram'], ir: ['i', 'iste', 'iu', 'imos', 'iram'],
};
const IMPF = { ar: ['ava', 'avas', 'ava', 'ávamos', 'avam'], er: ['ia', 'ias', 'ia', 'íamos', 'iam'], ir: ['ia', 'ias', 'ia', 'íamos', 'iam'] };
const MQP = { ar: ['ara', 'aras', 'ara', 'áramos', 'aram'], er: ['era', 'eras', 'era', 'êramos', 'eram'], ir: ['ira', 'iras', 'ira', 'íramos', 'iram'] };
const FUT = ['ei', 'ás', 'á', 'emos', 'ão'];
const COND = ['ia', 'ias', 'ia', 'íamos', 'iam'];
const SUBJ = { ar: ['e', 'es', 'e', 'emos', 'em'], er: ['a', 'as', 'a', 'amos', 'am'], ir: ['a', 'as', 'a', 'amos', 'am'] };
const SUBJ_IMP = { ar: ['asse', 'asses', 'asse', 'ássemos', 'assem'], er: ['esse', 'esses', 'esse', 'êssemos', 'essem'], ir: ['isse', 'isses', 'isse', 'íssemos', 'issem'] };
const INF = ['', 'es', '', 'mos', 'em'];

const TER = {
  pres: ['tenho', 'tens', 'tem', 'temos', 'têm'],
  imp: ['tinha', 'tinhas', 'tinha', 'tínhamos', 'tinham'],
  fut: ['terei', 'terás', 'terá', 'teremos', 'terão'],
  cond: ['teria', 'terias', 'teria', 'teríamos', 'teriam'],
  subj: ['tenha', 'tenhas', 'tenha', 'tenhamos', 'tenham'],
  subjImp: ['tivesse', 'tivesses', 'tivesse', 'tivéssemos', 'tivessem'],
  subjFut: ['tiver', 'tiveres', 'tiver', 'tivermos', 'tiverem'],
};
const comp = (t: keyof typeof TER) => (v: string, i: number) => compound(TER[t][i], participle(v));

function perfeito(v: string, i: number): string {
  const t = type(v);
  return (i === 0 && t === 'ar' ? soft(root(v)) : root(v)) + PERF[t][i];
}
function subjPres(v: string, i: number): string {
  const t = type(v);
  return (t === 'ar' ? soft(root(v)) : root(v)) + SUBJ[t][i];
}

const que = (_i: number, _a: string, who: string) => `que ${who}`;
const se = (_i: number, _a: string, who: string) => `se ${who}`;
const quando = (_i: number, _a: string, who: string) => `quando ${who}`;
const cmd = (i: number) => ['', '(tu)', '(você)', '(nós)', '(vocês)'][i];

function imperative(v: string, i: number): string[] | null {
  if (i === 1) return [ptPresent(v, 1).slice(0, -1)];   // fala, come, parte
  if (i >= 2) return [subjPres(v, i)];                    // fale, falemos, falem
  return null;
}

const TENSES: Tense[] = [
  { id: 'perfeito', title: 'Pretérito perfeito', blurb: 'falei, comeu, partimos…', verbs: ALL, forms: (v, i) => [perfeito(v, i)] },
  { id: 'imperfeito', title: 'Pretérito imperfeito', blurb: 'falava, comia, partíamos…', verbs: ALL,
    forms: (v, i) => [root(v) + IMPF[type(v)][i]] },
  { id: 'mais_que_perfeito', title: 'Mais-que-perfeito (simple, literary)', blurb: 'falara, comêramos…', verbs: ALL,
    forms: (v, i) => [root(v) + MQP[type(v)][i]] },
  { id: 'futuro', title: 'Futuro do presente', blurb: 'falarei, comerá, partirão…', verbs: ALL, forms: (v, i) => [v + FUT[i]] },
  { id: 'condicional', title: 'Futuro do pretérito (condicional)', blurb: 'falaria, comeríamos…', verbs: ALL, forms: (v, i) => [v + COND[i]] },
  { id: 'subj_presente', title: 'Presente do subjuntivo', blurb: 'que eu fale, que nós comamos…', verbs: ALL,
    forms: (v, i) => [subjPres(v, i)], who: que },
  { id: 'subj_imperfeito', title: 'Imperfeito do subjuntivo', blurb: 'se eu falasse, se comêssemos…', verbs: ALL,
    forms: (v, i) => [root(v) + SUBJ_IMP[type(v)][i]], who: se },
  { id: 'subj_futuro', title: 'Futuro do subjuntivo', blurb: 'quando eu falar, quando vocês comerem…', verbs: ALL,
    forms: (v, i) => [v + INF[i]], who: quando },
  { id: 'infinitivo_pessoal', title: 'Infinitivo pessoal', blurb: 'para nós falarmos…', verbs: ALL,
    forms: (v, i) => [v + INF[i]], who: (_i, _a, w) => `para ${w}` },
  { id: 'perfeito_composto', title: 'Pretérito perfeito composto', blurb: 'tenho falado…', verbs: REG_PP, forms: comp('pres') },
  { id: 'mqp_composto', title: 'Mais-que-perfeito composto', blurb: 'tinha falado…', verbs: REG_PP, forms: comp('imp') },
  { id: 'futuro_composto', title: 'Futuro composto', blurb: 'terei falado…', verbs: REG_PP, forms: comp('fut') },
  { id: 'condicional_composto', title: 'Condicional composto', blurb: 'teria falado…', verbs: REG_PP, forms: comp('cond') },
  { id: 'subj_perfeito', title: 'Pretérito perfeito do subjuntivo', blurb: 'que eu tenha falado…', verbs: REG_PP, forms: comp('subj'), who: que },
  { id: 'subj_mqp', title: 'Mais-que-perfeito do subjuntivo', blurb: 'se eu tivesse falado…', verbs: REG_PP, forms: comp('subjImp'), who: se },
  { id: 'subj_futuro_composto', title: 'Futuro composto do subjuntivo', blurb: 'quando eu tiver falado…', verbs: REG_PP, forms: comp('subjFut'), who: quando },
  { id: 'imperativo', title: 'Imperativo afirmativo', blurb: 'fala!, fale!, falemos!', verbs: ALL,
    people: [1, 2, 3, 4], forms: imperative, who: cmd },
  { id: 'imperativo_neg', title: 'Imperativo negativo', blurb: 'não fales, não falem…', verbs: ALL,
    people: [1, 2, 3, 4], forms: (v, i) => [subjPres(v, i)], who: i => `${cmd(i)} não` },
];

export const PT_TENSES: LanguageTenses = {
  lang: 'pt',
  people: [['eu'], ['tu'], ['você', 'ele', 'ela'], ['nós'], ['vocês', 'eles', 'elas']],
  optional: 1,
  tenses: TENSES,
};
